import React, { useState, useCallback, useEffect } from 'react';

import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/js/plugins.pkgd.min.js';
import 'froala-editor/js/languages/es';
import FroalaEditor from 'react-froala-wysiwyg';

export default function RichTextEditor({ value, onSave, onCancel, froalaConfig }) {
  const [val, setVal] = useState(value || '');

  const handleModelChange = useCallback((model) => {
    setVal(model);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  // Exact same config as old Surveys/Index.jsx desktop config
  const config = {
    language: 'es',
    quickInsertEnabled: false,
    colorsHEXInput: true,
    autoFocus: true,
    toolbartop: true,
    linkAlwaysBlank: true,
    fontFamilySelection: true,
    fontSizeSelection: true,
    paragraphFormatSelection: true,
    htmlExecuteScripts: true,
    key: froalaConfig?.key || '',
    imageUploadToS3: froalaConfig?.imageUpload || undefined,
    iframe: true,
    // Froala usa iframe, asi que el CSS del parent NO afecta el contenido editable.
    // Inyectamos directamente en el iframe para forzar wrap de URLs/texto largo y
    // evitar que el contenido se salga del modal.
    iframeStyle: `
      html, body {
        max-width: 100% !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
        overflow-x: hidden !important;
        box-sizing: border-box;
      }
      body * {
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      p, div, span, li, td, th, h1, h2, h3, h4, h5, h6, blockquote {
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
      }
      img { max-width: 100% !important; height: auto !important; }
      table { width: 100% !important; table-layout: fixed !important; }
      a { word-break: break-all !important; }
      pre, code { white-space: pre-wrap !important; word-break: break-word !important; }
    `,
    tabSpaces: 4,
    imagePaste: false,
    heightMin: 200,
    heightMax: 400,
    events: {
      'image.beforeUpload': function (files) {
        const file = files[0];
        if (!file) return true;
        if (file.type === 'image/webp' || file.name?.toLowerCase().endsWith('.webp') ||
            file.type === 'image/heic' || file.name?.toLowerCase().endsWith('.heic') ||
            file.type === 'image/heif' || file.name?.toLowerCase().endsWith('.heif')) {
          alert('Este formato de imagen no es compatible con los PDFs.\n\nFormatos soportados: JPG, JPEG, PNG, GIF');
          return false;
        }
        return true;
      }
    },
    pluginsEnabled: ['align', 'charCounter', 'codeBeautifier', 'codeView', 'colors', 'draggable', 'embedly', 'emoticons', 'entities', 'fontFamily', 'fontSize', 'fullscreen', 'image', 'imageManager', 'inlineStyle', 'lineBreaker', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quickInsert', 'quote', 'save', 'table', 'url', 'wordPaste'],
    toolbarButtons: {
      moreText: {
        buttons: ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor', 'inlineClass', 'inlineStyle', 'clearFormatting'],
        align: 'left',
        buttonsVisible: 12
      },
      moreParagraph: {
        buttons: ['alignLeft', 'alignCenter', 'formatOLSimple', 'alignRight', 'alignJustify', 'formatOL', 'formatUL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
        align: 'left',
        buttonsVisible: 11
      },
      moreRich: {
        buttons: ['insertLink', 'insertImage', 'insertVideo', 'insertTable', 'emoticons', 'fontAwesome', 'specialCharacters', 'embedly', 'insertFile', 'insertHR'],
        align: 'left',
        buttonsVisible: 10
      },
      moreMisc: {
        buttons: ['undo', 'redo', 'fullscreen', 'print', 'spellChecker', 'selectAll', 'html', 'help'],
        align: 'right',
        buttonsVisible: 9
      }
    }
  };

  return (
    <div className="cm-modal-overlay" onClick={onCancel}>
      <div className="cm-modal-richtext" onClick={e => e.stopPropagation()}>
        <div className="cm-modal-richtext__header">
          <span className="cm-modal-richtext__title">Editar texto</span>
          <button className="cm-modal-richtext__close" onClick={onCancel}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="cm-modal-richtext__body">
          <FroalaEditor
            model={val}
            config={config}
            onModelChange={handleModelChange}
          />
        </div>
        <div className="cm-modal-richtext__footer">
          <button className="cm-btn cm-btn--secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button className="cm-btn cm-btn--primary" onClick={() => onSave(val)}>
            <i className="fas fa-check" /> Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
